import {
  browserSupportsWebAuthnAutofill,
  startAuthentication,
  WebAuthnAbortService,
} from "@simplewebauthn/browser";
import { useMutation } from "@tanstack/react-query";
import { useServerFn } from "#/modules/util.hooks/use-server-fn";
import { useNavigate } from "@tanstack/react-router";
import { Button } from "@beweegbaas/ui";
import { toast } from "@beweegbaas/ui";
import { useEffect, useEffectEvent, useState } from "react";
import { m } from "#/paraglide/messages";
import { captureUnexpectedError } from "#/modules/util.errors/capture";
import { isWebAuthnCancellation, isWebAuthnUnavailable } from "#/modules/util.errors/expected";
import { usePasskeySupport } from "#/modules/util.auth/passkey-support";
import type { LoginRedirect } from "#/modules/util.auth/authenticate.server";
import {
  passkeyLoginRequestOptionsFn,
  passkeyLoginVerifyFn,
} from "#/modules/util.auth/passkey.functions";

/** Custom error class so we can distinguish user-facing server errors from browser errors. */
class PasskeyError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "PasskeyError";
  }
}

/**
 * The passkey sign-in on the login page: passkeys offered in the email field's autofill
 * (`autocomplete="username webauthn"`) plus a button. A device known to lack passkey support gets
 * neither.
 */
export function PasskeyLogin({ referer }: { referer?: string }) {
  const navigate = useNavigate();
  const requestOptions = useServerFn(passkeyLoginRequestOptionsFn);
  const verifyLogin = useServerFn(passkeyLoginVerifyFn);
  const passkeySupport = usePasskeySupport();
  const [verifyingAutofill, setVerifyingAutofill] = useState(false);
  // Bumped to offer passkeys in autofill again once a button ceremony took over and ended.
  const [autofillRound, setAutofillRound] = useState(0);

  async function signIn(useBrowserAutofill: boolean): Promise<LoginRedirect | undefined> {
    // Step 1 — get options from backend (also stashes the Microsoft Identity cookie
    //           in the short-lived passkey session server-side).
    const optionsResult = await requestOptions({});
    if (!optionsResult) return;
    if ("formError" in optionsResult) {
      throw new PasskeyError(optionsResult.formError);
    }
    if (!optionsResult.options) {
      throw new Error("No request options received");
    }

    // Step 2 — hand off to the browser's WebAuthn ceremony. With autofill this waits until
    //           the user picks a passkey in the email field's suggestions.
    const credential = await startAuthentication({
      optionsJSON: optionsResult.options,
      useBrowserAutofill,
    });
    if (useBrowserAutofill) setVerifyingAutofill(true);

    // Step 3 — send the signed credential to the server for verification. The referer
    // (attempted URL, e.g. an invite accept link) rides along so the login returns there.
    const verifyResult = await verifyLogin({
      data: { credentials: JSON.stringify(credential), referer },
    });
    if (!verifyResult) return;
    if ("formError" in verifyResult) {
      throw new PasskeyError(verifyResult.formError);
    }

    return verifyResult.redirect;
  }

  function handleError(error: Error, flow: "passkey-login" | "passkey-autofill") {
    if (error instanceof PasskeyError) {
      toast.error(error.message);
      return;
    }
    captureUnexpectedError(error, flow);
    // Cancelled, no passkey on this device or timed out: browsers report these alike on
    // purpose, and their own prompt already told the user.
    if (isWebAuthnCancellation(error)) return;
    if (isWebAuthnUnavailable(error)) {
      // The autofill offer fails unseen; only a click deserves an answer.
      if (flow === "passkey-login") toast.error(m["auth.passkey.unavailable_use_password"]());
      return;
    }
    toast.error(m["auth.passkey.failed_to_login"]());
  }

  const mutation = useMutation({
    mutationFn: () => signIn(false),
    onSuccess: (redirect) => {
      if (redirect) void navigate(redirect);
    },
    onError: (error) => {
      handleError(error, "passkey-login");
      setAutofillRound((round) => round + 1);
    },
  });

  const offerInAutofill = useEffectEvent(async () => {
    if (!(await browserSupportsWebAuthnAutofill())) return;
    try {
      const redirect = await signIn(true);
      if (redirect) void navigate(redirect);
    } catch (error) {
      setVerifyingAutofill(false);
      if (!(error instanceof Error)) throw error;
      // The button's ceremony or leaving the page replaced the autofill offer.
      if (error.name === "AbortError") return;
      handleError(error, "passkey-autofill");
    }
  });

  const { isSupported } = passkeySupport;
  useEffect(() => {
    if (!isSupported) return;
    void offerInAutofill();
    return () => WebAuthnAbortService.cancelCeremony();
  }, [isSupported, autofillRound]);

  if (!isSupported) return null;

  return (
    <>
      <div className="my-4 flex items-center gap-3 text-xs text-[var(--sea-ink-soft)]">
        <span className="h-px flex-1 bg-[var(--line)]" />
        of
        <span className="h-px flex-1 bg-[var(--line)]" />
      </div>
      <Button
        type="button"
        variant="secondary"
        className="w-full"
        isPending={mutation.isPending || verifyingAutofill}
        onClick={() => mutation.mutate()}
      >
        {m["auth.passkey.login_with_passkey"]()}
      </Button>
    </>
  );
}
